/** @jsxImportSource @gongxigames/framework/core/fragments/BuilderY */
/** @jsxRuntime automatic */

import {FC, CanBeSignals, mapProp, getProp, $SetVisibility, $Sprite, $Container, ContainerProps, $Button, RichTextStyleParams} from '@gongxigames/framework/core/fragments/BuilderY';
import {$RubyUIText} from '@gongxigames/framework/slotV2/ui/ui-ruby/JSX/RubyUIText';
import {Store} from '@gongxigames/framework/core';
import {Signal} from '@gongxigames/framework/core';
import {Circle} from 'pixi.js';

export type BtnMode = 'base' | 'free_spins' | 'autoplay';
export type SpinBtnState = 'default' | 'skip' | 'quickStop';

export type SpinBtnProps = ContainerProps & CanBeSignals<{
    jurisdictionZoneHeight: number;
    mode: BtnMode;
    disabled: boolean;
    autoplayRoundsLeft: number;
    fsTotal: number;
    fsCurrent: number;
    pointerDown: () => void;
    pointerUp: (over: boolean) => void;
}>;

const HIT_AREA = new Circle(0, 0, 94*3);

export const $SpinBtn: FC<SpinBtnProps> = (props) => {
    const {mode} = props;
    const autoSpinsLeft = mapProp(props.autoplayRoundsLeft, String);
    const fsLeft = Store.computed(() => `${getProp(props.fsTotal) - getProp(props.fsCurrent)}`);

    return <$Button
        {...props}
        name='spin_btn'
        hitArea={HIT_AREA}
        pointerDown={props.pointerDown}
        pointerUp={props.pointerUp}
        disabled={props.disabled}
    >{(props) => {
            const fsLeftCountColor: Signal<RichTextStyleParams> = mapProp(props.focused, focused => ({
                fill: focused ? 0xffffff : 0xF2FFEB,
                dropShadowColor: focused ? 0x59FF22 : 0x27D500,
                dropShadowBlur: 10,
                dropShadow: true,
                dropShadowDistance: 0,
            }));

            return <$SetVisibility selector={mode}>
                <$Container name='base'>
                    <$SetVisibility selector={props.selector}>
                        <$Sprite anchor={0.5} name='normal' texture='spin_btn_bg2_normal_sys_ui'>
                            <$Sprite anchor={0.5} name='base' texture='spin_btn_text_normal_sys_ui'/>
                            <$Sprite anchor={0.5} name='base' texture='spin_btn_icon_sys_ui'/>
                        </$Sprite>
                        <$Sprite anchor={0.5} name='focused' texture='spin_btn_bg2_normal_sys_ui'>
                            <$Sprite anchor={0.5} name='base' texture='spin_btn_text_normal_sys_ui'/>
                            <$Sprite anchor={0.5} name='base' texture='spin_btn_icon_hover_sys_ui'/>
                        </$Sprite>
                        <$Sprite anchor={0.5} name='pressed' texture='spin_btn_bg2_pressed_sys_ui'>
                            <$Sprite anchor={0.5} name='base' texture='spin_btn_text_normal_sys_ui'/>
                            <$Sprite anchor={0.5} name='base' texture='spin_btn_icon_sys_ui'/>
                        </$Sprite>
                        <$Sprite anchor={0.5} name='disabled' texture='spin_btn_bg2_normal_sys_ui' alpha={0.28}>
                            <$Sprite anchor={0.5} name='base' texture='spin_btn_text_normal_sys_ui'/>
                            <$Sprite anchor={0.5} name='base' texture='spin_btn_icon_sys_ui'/>
                        </$Sprite>
                    </$SetVisibility>
                </$Container>

                <$Container name='autoplay'>
                    <$SetVisibility selector={props.selector}>
                        <$Sprite anchor={0.5} name='normal' texture='stop_autoplay_btn_bg2_normal_sys_ui'>
                            <$Sprite anchor={0.5} name='base' texture='stop_autoplay_btn_text_normal_sys_ui'/>
                            <$Sprite anchor={0.5} name='base' texture='stop_autoplay_btn_icon_sys_ui'/>
                        </$Sprite>
                        <$Sprite anchor={0.5} name='focused' texture='stop_autoplay_btn_bg2_normal_sys_ui'>
                            <$Sprite anchor={0.5} name='base' texture='stop_autoplay_btn_text_normal_sys_ui'/>
                            <$Sprite anchor={0.5} name='base' texture='stop_autoplay_btn_icon_hover_sys_ui'/>
                        </$Sprite>
                        <$Sprite anchor={0.5} name='pressed' texture='stop_autoplay_btn_bg2_pressed_sys_ui'>
                            <$Sprite anchor={0.5} name='base' texture='stop_autoplay_btn_text_normal_sys_ui'/>
                            <$Sprite anchor={0.5} name='base' texture='stop_autoplay_btn_icon_sys_ui'/>
                        </$Sprite>
                        <$Sprite anchor={0.5} name='disabled' texture='stop_autoplay_btn_bg2_normal_sys_ui' alpha={0.28}>
                            <$Sprite anchor={0.5} name='base' texture='stop_autoplay_btn_text_normal_sys_ui'/>
                            <$Sprite anchor={0.5} name='base' texture='stop_autoplay_btn_icon_sys_ui'/>
                        </$Sprite>
                    </$SetVisibility>
                    <$RubyUIText
                        y={4}
                        maxWidth={110}
                        name='spins_left_count'
                        stylePreset='SPIN_BTN_AUTOPLAY_LEFT'
                        text={autoSpinsLeft}
                    />
                </$Container>

                <$Container name='free_spins'>
                    <$SetVisibility selector={props.selector}>
                        <$Sprite anchor={0.5} name='normal' texture='spin_btn_bg2_normal_sys_ui'/>
                        <$Sprite anchor={0.5} name='focused' texture='spin_btn_bg2_normal_sys_ui'/>
                        <$Sprite anchor={0.5} name='pressed' texture='spin_btn_bg2_pressed_sys_ui'/>
                        <$Sprite anchor={0.5} name='disabled' texture='spin_btn_bg2_normal_sys_ui' alpha={0.28}/>
                    </$SetVisibility>
                    <$RubyUIText
                        y={64}
                        maxWidth={180}
                        name='spins_left_count'
                        stylePreset='BASE_BOLD'
                        localeID='spinBtnSpinsLeftLabel'
                        style={{fontSize: 16*3}}
                    />
                    <$RubyUIText
                        y={-50}
                        maxWidth={180}
                        name='spins_left_count'
                        stylePreset='SPIN_BTN_FS_LEFT'
                        text={fsLeft}
                        style={fsLeftCountColor}
                    />
                </$Container>
            </$SetVisibility>;
        }}
    </$Button>;
};